import type { ComponentType } from 'react';
import type { Locale, PageId } from '@nexa/content';

/**
 * Bản đồ id trang → module. Mỗi specifier phải là chuỗi HẰNG viết tay: bundler cần biết
 * trước lúc build để tách chunk, nên `import(\`./pages/${id}\`)` sẽ hoặc gom cả thư mục vào
 * một chunk, hoặc không tách được gì.
 *
 * Đây cũng là chỗ TypeScript ép đủ: thêm một `PageId` mà quên component thì `Record` đỏ ngay.
 */
type PageModule = () => Promise<{ readonly default: ComponentType }>;

/**
 * Khoá theo `(PageId, Locale)`, không chỉ `PageId` — vé #18.
 *
 * Trang pháp lý là 99,86% CHỮ, và một component nạp cả hai `?raw` hằng thì bundler buộc phải
 * gộp hai locale vào một chunk: `/vi/privacy/` tải 170.963 B để render 94.753 B. Tách wrapper
 * theo locale là cách DUY NHẤT giữ được cả ba ràng buộc — specifier HẰNG, route lazy per-page,
 * không barrel. `import()` động hoặc `index.ts` đều làm chunk gộp lại.
 *
 * `Record<Locale, …>` cũng là chỗ TypeScript ép đủ lần thứ hai: thêm một `Locale` mà quên
 * wrapper thì đỏ ngay, y như thêm `PageId` mà quên component.
 *
 * ⛔ ĐỪNG bỏ `html` để dùng lại DOM đã prerender: đã đo ở vé #18 — React GỠ con của node
 * `dangerouslySetInnerHTML` khi hydrate mismatch (`#418`) ⇒ 14 trang pháp lý TRẮNG, trong khi mọi
 * phép đo TĨNH vẫn xanh. `suppressHydrationWarning` không cứu.
 *
 * DIỆN của cổng canh nó, khai ở đây vì đây là chỗ DUY NHẤT — 14 bản sao docblock cũ ở wrapper chỉ
 * ghi "đã đo (vé #18)" rồi trôi khỏi sự thật: `apps/web/script/hydrate-probe.mjs` canh
 * **chuỗi có được CẤP hay không**, KHÔNG canh **chuỗi có ĐÚNG hay không**. Vé #37 đo bằng hai
 * đột biến — prop VẮNG lúc hydrate ⇒ 14/14 hỏng, BẮT được; `__html` là chuỗi rỗng ⇒ 14/14 "sống
 * sót", KHÔNG bắt được (hydrate, React TIN HTML của server cho node này nên không có mismatch).
 *
 * Bốn diện, không cái nào thay được cái nào: `hydrate-probe` (chuỗi có được cấp) · `check:legal`
 * (nội dung đúng, đọc `dist`) · `htmlLocale` trong `LegalDocPage` (đúng locale, ném lúc prerender)
 * · `pageModules.test.ts` (wrapper nhập ĐÚNG FILE — trục mà ba cái trên mù, trừ `check:legal` vốn
 * phải build + prerender mới thấy; vé #39).
 *
 * `home` khai cùng một specifier cho cả hai locale — CÙNG chunk, không nhân bản gì: trang chủ
 * lấy chữ từ `i18n`, không nhúng `?raw` nào.
 */
export const PAGE_MODULE: Record<PageId, Record<Locale, PageModule>> = {
  home: {
    vi: () => import('@/pages/HomePage.tsx'),
    en: () => import('@/pages/HomePage.tsx'),
  },

  'legal-cookies': {
    vi: () => import('@/pages/legal/CookiesPage.vi.tsx'),
    en: () => import('@/pages/legal/CookiesPage.en.tsx'),
  },
  'legal-delete-account': {
    vi: () => import('@/pages/legal/DeleteAccountPage.vi.tsx'),
    en: () => import('@/pages/legal/DeleteAccountPage.en.tsx'),
  },
  'legal-privacy': {
    vi: () => import('@/pages/legal/PrivacyPage.vi.tsx'),
    en: () => import('@/pages/legal/PrivacyPage.en.tsx'),
  },
  'legal-privacy-v1': {
    vi: () => import('@/pages/legal/PrivacyV1Page.vi.tsx'),
    en: () => import('@/pages/legal/PrivacyV1Page.en.tsx'),
  },
  'legal-support': {
    vi: () => import('@/pages/legal/SupportPage.vi.tsx'),
    en: () => import('@/pages/legal/SupportPage.en.tsx'),
  },
  'legal-terms': {
    vi: () => import('@/pages/legal/TermsPage.vi.tsx'),
    en: () => import('@/pages/legal/TermsPage.en.tsx'),
  },
  'legal-terms-v1': {
    vi: () => import('@/pages/legal/TermsV1Page.vi.tsx'),
    en: () => import('@/pages/legal/TermsV1Page.en.tsx'),
  },
};
